import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { X, Send, Loader2, Minimize2, Maximize2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import aiLogo from '@/assets/ai-assistant-logo.png';
import IntelligenceWorkspace from '@/components/intelligence/IntelligenceWorkspace';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const PUBLIC_ROUTES = ['/', '/features', '/pricing', '/about', '/industries', '/contact', '/security', '/auth', '/privacy-policy', '/reset-password', '/portal'];

const SUGGESTIONS = [
  "Show unpaid invoices",
  "Forecast next month revenue",
  "Which clients are most active?",
  "How many tasks are overdue?",
  "Show lead pipeline summary",
  "Team attendance today",
];

export function AIAssistantPanel() {
  const { user, role } = useAuth();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async (text?: string) => {
    const msgText = text || input.trim();
    if (!msgText || loading) return;

    const userMsg: Message = { role: 'user', content: msgText };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('ai-assistant', {
        body: {
          query: userMsg.content,
          conversationHistory: messages.slice(-8),
        },
      });

      if (error) throw error;

      if (data?.error) {
        if (data.error.includes('429') || data.error.includes('Rate limit')) {
          toast.error('AI rate limit reached. Please try again in a minute.');
        } else if (data.error.includes('402')) {
          toast.error('AI credits exhausted. Please add credits.');
        } else {
          throw new Error(data.error);
        }
        return;
      }

      setMessages(prev => [...prev, { role: 'assistant', content: data?.response || 'No response' }]);
    } catch (err: any) {
      console.error('AI Assistant error:', err);
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  const isPublicPage = PUBLIC_ROUTES.some(route => 
    location.pathname === route || location.pathname.startsWith('/portal/')
  );
  
  if (!user || role === 'superadmin' || isPublicPage) return null;

  // Full Intelligence Workspace mode
  if (isFullscreen) {
    return <IntelligenceWorkspace onClose={() => setIsFullscreen(false)} />;
  }

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 h-10 w-10 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105 flex items-center justify-center overflow-hidden"
          aria-label="Open AI Assistant"
        >
          <img src={aiLogo} alt="AI Assistant" className="h-10 w-10 object-cover" />
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <Card className="fixed bottom-6 right-6 z-50 w-[400px] h-[560px] flex flex-col shadow-2xl border">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b bg-primary/5">
            <div className="flex items-center gap-2">
              <img src={aiLogo} alt="AI" className="h-6 w-6 rounded-full object-cover" />
              <div>
                <h3 className="font-semibold text-sm">Clicknify Intelligence</h3>
                <p className="text-xs text-muted-foreground">Enterprise AI Engine</p>
              </div>
            </div>
            <div className="flex gap-1">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setIsOpen(false); setIsFullscreen(true); }} title="Open Intelligence Workspace">
                <Maximize2 className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setIsOpen(false)}>
                <Minimize2 className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setIsOpen(false); setMessages([]); }}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Messages */}
          <ScrollArea className="flex-1 p-4" ref={scrollRef}>
            {messages.length === 0 && (
              <div className="text-center py-6">
                <img src={aiLogo} alt="AI" className="h-10 w-10 mx-auto mb-3 rounded-full opacity-50" />
                <p className="text-sm text-muted-foreground mb-4">Try asking:</p>
                <div className="space-y-2">
                  {SUGGESTIONS.map((suggestion) => (
                    <button
                      key={suggestion}
                      onClick={() => sendMessage(suggestion)}
                      className="block w-full text-left text-xs p-2 rounded-md bg-muted/50 hover:bg-muted transition-colors text-muted-foreground"
                    >
                      "{suggestion}"
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-4">
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[90%] rounded-lg px-3 py-2 text-sm ${
                    msg.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground'
                  }`}>
                    {msg.role === 'assistant' ? (
                      <div className="prose prose-sm dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0.5 [&_h1]:text-sm [&_h2]:text-sm [&_h3]:text-xs [&_table]:text-xs">
                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                      </div>
                    ) : (
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-muted rounded-lg px-3 py-2 flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Analyzing your data...</span>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Input */}
          <div className="p-3 border-t">
            <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="flex gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask Clicknify Intelligence..."
                className="flex-1 text-sm"
                disabled={loading}
              />
              <Button type="submit" size="icon" disabled={loading || !input.trim()}>
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </div>
        </Card>
      )}
    </>
  );
}
